<template>
    <div class="role-chips" role="group" :aria-label="groupLabel" data-testid="user-role-chips">
        <!--
            ОДИН чип, три взаимоисключающих состояния (редизайн 29.05.2026):
              • student            → «Ученик» (нейтрально-синий, статус, без
                                      tooltip и без интерактива);
              • mentor_unconfirmed → «Наставник» СЕРЫЙ + tooltip: ещё не
                                      подтверждён, запись на сессии после
                                      модерации;
              • mentor_confirmed   → «Наставник» цветной (подтверждён).
            Никакой стрелки и фазовых подписей («· черновик», «· на проверке»…) —
            «не подтверждён» выражается цветом, детали — в tooltip.
        -->

        <!-- Ученик — статус-индикатор, не кнопка, без подсказки. -->
        <span
            v-if="displayState === 'student'"
            class="role-chip role-chip--student role-chip--active"
            aria-current="true"
            data-testid="role-chip-student"
        >
            Ученик
        </span>

        <!--
            Наставник (подтверждён / не подтверждён) — кнопка с tooltip.
            v-model (tooltipOpen) нужен для мобильного (compact) сценария: на
            touch нет hover, поэтому tooltip раскрываем программно по тапу.
            На десктопе (не compact) — прежнее hover/focus-поведение.
        -->
        <v-tooltip
            v-else
            v-model="tooltipOpen"
            location="bottom"
            :open-on-focus="!compact"
            :open-on-hover="!compact"
        >
            <template #activator="{ props: tooltipProps }">
                <button
                    ref="mentorChipRef"
                    v-bind="tooltipProps"
                    type="button"
                    class="role-chip role-chip--mentor"
                    :class="[mentorChipClass, { 'role-chip--payout-alert': isPayoutAlert }]"
                    :aria-label="mentorAriaLabel"
                    :aria-describedby="tooltipId"
                    :aria-current="isConfirmed ? 'true' : undefined"
                    :aria-busy="isPending ? 'true' : undefined"
                    :aria-expanded="compact ? String(tooltipOpen) : undefined"
                    data-testid="role-chip-mentor"
                    @click="onChipClick"
                    @keydown="onChipKeydown"
                >
                    <span
                        v-if="isPending"
                        class="role-chip__dot role-chip__dot--pulse"
                        aria-hidden="true"
                        data-testid="role-chip-pulse"
                    />
                    <!--
                        Мобильный (compact) индикатор «карта не привязана».
                        Десктопный суффикс «· Подключить карту» на узком экране
                        скрыт (HeaderDefault), поэтому «нужно действие» несём
                        оранжевым чипом (role-chip--payout-alert) + МИГАЮЩЕЙ
                        точкой. На десктопе не рендерим: там виден полный суффикс.
                    -->
                    <span
                        v-if="isPayoutAlert"
                        class="role-chip__dot role-chip__dot--payout"
                        aria-hidden="true"
                        data-testid="role-chip-payout-dot"
                    />
                    <span class="role-chip__label">Наставник</span>
                    <!--
                        Краткий статус-суффикс для НЕподтверждённого наставника —
                        чтобы пользователю было сразу понятно, на каком он этапе
                        (особенно «заявка не подана», когда он не дозаполнил).
                        Для action-needed состояний (draft/changes/rejected) —
                        янтарный, как «нужно ваше действие».
                    -->
                    <span
                        v-if="mentorSuffix"
                        class="role-chip__suffix"
                        :class="{ 'role-chip__suffix--action': suffixNeedsAction }"
                        data-testid="role-chip-suffix"
                        >· {{ mentorSuffix }}</span
                    >
                </button>
            </template>

            <div :id="tooltipId" class="role-chip-tooltip" data-testid="role-chip-tooltip">
                <p class="role-chip-tooltip__text">{{ tooltipMessage }}</p>
            </div>
        </v-tooltip>

        <!--
            Модалка статуса заявки. Открывается по клику на чип в фазе
            «на проверке» (pending) — пользователю нечего делать, поэтому
            вместо навигации показываем понятный статус. Самодостаточна,
            mobile-first (bottom-sheet на узких экранах).
        -->
        <MentorApplicationStatusModal
            v-model="statusModalOpen"
            :phase="applicationPhase"
            :rejection-reason="rejectionReason"
        />
    </div>
</template>

<script setup lang="ts">
/**
 * UserRoleChips — единый чип статуса пользователя на платформе.
 *
 * Pure presentational (audit §2.2 "Container/Presentational"): не читает
 * Vuex/composables напрямую, не ходит в API. Контейнер (HeaderDefault,
 * DashboardPage, ProfileMenu) подтягивает `useMentorApplicationStatus` и
 * прокидывает уже нормализованные props.
 *
 * Модель состояний (редизайн по требованию 29.05.2026) — ТРИ статуса:
 *   1. «Ученик»                — нет активной заявки наставника.
 *   2. «Наставник» (серый)     — заявка в процессе / на модерации / отклонена,
 *                                т.е. наставник «не подтверждён».
 *   3. «Наставник» (цветной)   — заявка одобрена, полноценный наставник.
 *
 * Никаких фазовых подписей в самой надписи чипа — различие «подтверждён /
 * не подтверждён» передаётся ЦВЕТОМ, детали — в tooltip.
 *
 * A11y (audit §3 — WCAG 2.1 AA):
 *   - role="group" + aria-label на контейнере;
 *   - «Ученик» — неинтерактивный <span> со status-семантикой (aria-current);
 *   - «Наставник» — настоящий <button type="button"> с aria-label,
 *     aria-describedby (id тела tooltip), aria-current (подтверждён),
 *     aria-busy (на модерации);
 *   - pulse-dot декоративный (aria-hidden), анимация отключается при
 *     prefers-reduced-motion;
 *   - tooltip закрывается по Escape (Vuetify v3 для button-activator),
 *     дополнительно focus() на чип как защитная мера.
 */
import { computed, ref } from 'vue';

import type { Phase } from '@/composables/useMentorApplicationStatus';
import MentorApplicationStatusModal from '@/components/common/MentorApplicationStatusModal.vue';

interface Props {
    /** Текущая роль из user-записи (Vuex `userRole`). */
    currentRole: 'mentor' | 'student';
    /** Нормализованная фаза заявки из useMentorApplicationStatus(). */
    applicationPhase: Phase;
    /** Причина отказа от модератора (используется только при phase=rejected). */
    rejectionReason?: string;
    /**
     * 2026-06-01 hotfix: одобренный наставник, но способ выплат ещё не подключён
     * (``selling_readiness === 'approved_no_payout'``). Заменяет собой
     * прежний sticky-бар, который занимал целую строку под header'ом. В этом
     * состоянии на ДЕСКТОПЕ чип остаётся «подтверждённым» (фиолетовая заливка) и
     * получает янтарный action-суффикс «· Подключить карту»; на МОБИЛЬНОМ
     * (compact) — оранжевый чип + мигающая точка (P3, см. ``isPayoutAlert``).
     * Клик ведёт в настройки приёма денег. Контейнер вычисляет флаг из
     * ``useSellingReadiness().value``.
     */
    payoutMissing?: boolean;
    /**
     * Компактный (мобильный) режим. Контейнер выставляет на узком вьюпорте
     * (HeaderDefault → ``smAndDown``), где суффикс чипа скрыт по ширине.
     * В этом режиме:
     *   • payout-missing показываем ОРАНЖЕВЫМ чипом + МИГАЮЩЕЙ точкой (P3),
     *     т.к. текстовый суффикс скрыт по ширине;
     *   • hover/focus-раскрытие tooltip отключаем (на touch их нет) и вместо
     *     навигации первый тап по payout-чипу РАСКРЫВАЕТ tooltip с объяснением,
     *     а повторный — ведёт в настройки выплат.
     */
    compact?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    rejectionReason: '',
    payoutMissing: false,
    compact: false,
});

const emit = defineEmits<{
    (e: 'click-mentor-chip', phase: Phase): void;
    /**
     * Клик на пилюлю в состоянии «mentor_confirmed + payoutMissing».
     * Отдельный канал, чтобы контейнер мог навигировать на
     * ``/ProfileSettings?tab=payment`` без расширения Phase-енума.
     */
    (e: 'click-payout-attach'): void;
}>();

const mentorChipRef = ref<HTMLButtonElement | null>(null);

// Открыта ли модалка статуса заявки (открывается по клику на чип «на проверке»).
const statusModalOpen = ref(false);

// Управляемое состояние tooltip. На десктопе им «рулит» Vuetify (hover/focus);
// в compact-режиме — мы сами, чтобы раскрыть подсказку по тапу (touch без hover).
const tooltipOpen = ref(false);

// Стабильный per-instance id для aria-describedby. Math.random дёшев против
// импорта nanoid ради header-виджета; коллизии между инстансами на странице
// не влияют на корректность (id только связывает SR-described-by).
const tooltipId = `role-chip-mentor-tooltip-${Math.random().toString(36).slice(2, 10)}`;

const groupLabel = 'Ваш статус на платформе';

// ── Единое состояние отображения ──────────────────────────────────────

type DisplayState = 'student' | 'mentor_unconfirmed' | 'mentor_confirmed';

/**
 * Маппинг (role, phase) → одно из трёх взаимоисключающих состояний.
 *
 *   revoked (перебивает role=mentor)               → mentor_unconfirmed
 *   approved | role=mentor                         → mentor_confirmed
 *   draft | pending | changes_requested | rejected → mentor_unconfirmed
 *   none | cancelled                               → student
 *
 * `rejected` оставлен в «не подтверждён» (а не в «ученик»), чтобы статус
 * заявки и её причина оставались видимыми в tooltip — пользователь ещё в
 * процессе становления наставником и может подать заново.
 *
 * `revoked` проверяется ПЕРВЫМ: у отозванного админом наставника роль в
 * user-записи остаётся `mentor` (снимается только selling_readiness), и без
 * этого приоритета он видел бы фиолетовый «профиль подтверждён».
 */
const displayState = computed<DisplayState>(() => {
    if (props.applicationPhase === 'revoked') {
        return 'mentor_unconfirmed';
    }
    if (props.currentRole === 'mentor' || props.applicationPhase === 'approved') {
        return 'mentor_confirmed';
    }
    if (
        props.applicationPhase === 'draft' ||
        props.applicationPhase === 'pending' ||
        props.applicationPhase === 'changes_requested' ||
        props.applicationPhase === 'rejected'
    ) {
        return 'mentor_unconfirmed';
    }
    return 'student';
});

const isConfirmed = computed<boolean>(() => displayState.value === 'mentor_confirmed');

const isPending = computed<boolean>(
    () => props.applicationPhase === 'pending' || props.applicationPhase === 'changes_requested',
);

// ── Презентация чипа «Наставник» ──────────────────────────────────────

const mentorChipClass = computed<string>(() =>
    isConfirmed.value ? 'role-chip--confirmed' : 'role-chip--unconfirmed',
);

/**
 * P3 (report4-14-07-26): компактный (мобильный) чип подтверждённого наставника
 * без привязанной карты. По требованию заказчика в этом состоянии чип —
 * тёплый ОРАНЖЕВЫЙ (перебивает фиолетовый confirmed) с МИГАЮЩЕЙ точкой:
 * на узком экране текстовый суффикс «· Подключить карту» скрыт (HeaderDefault),
 * и нужен заметный сигнал «нужно действие». На десктопе (не compact) НЕ
 * применяется — там остаётся фиолетовый confirmed + видимый суффикс (#250).
 */
const isPayoutAlert = computed<boolean>(
    () => isConfirmed.value && props.payoutMissing && props.compact,
);

/**
 * Краткий статус-суффикс к надписи «Наставник» для НЕподтверждённого
 * наставника. Цель — чтобы пользователю было сразу ПОНЯТНО, на каком он
 * этапе, прямо в чипе (без наведения):
 *   • «заявка не подана» — он начал, но не дозаполнил/не отправил (draft);
 *   • «на проверке»      — отправил, ждёт модерацию (submitted/pending);
 *   • «нужны правки»     — модератор вернул на доработку;
 *   • «отклонена»        — отказ.
 * Для подтверждённого наставника суффикса нет — просто «Наставник».
 */
const mentorSuffix = computed<string>(() => {
    // Confirmed + payout missing — единственный случай, когда у подтверждённого
    // наставника есть суффикс. Прежде эта информация жила в отдельной sticky-полосе
    // под header'ом; теперь конденсирована в пилюлю.
    if (isConfirmed.value) {
        // Побуждающая формулировка (баг 9.1) — консистентно с
        // MentorNoPayoutBanner.vue и PayoutPage.vue: зовём к действию,
        // а не констатируем «карта не привязана».
        return props.payoutMissing ? 'Привязать карту' : '';
    }
    switch (props.applicationPhase) {
        case 'draft':
            return 'заявка не подана';
        case 'pending':
            return 'на проверке';
        case 'changes_requested':
            return 'нужны правки';
        case 'rejected':
            return 'отклонена';
        case 'revoked':
            return 'доступ отозван';
        default:
            return 'заявка не подана';
    }
});

/**
 * Состояния, где нужно ДЕЙСТВИЕ пользователя (дозаполнить / поправить /
 * подать заново / привязать карту) — суффикс подсвечивается янтарным,
 * чтобы привлечь внимание. «на проверке» — это ожидание, не действие →
 * нейтральный.
 */
const suffixNeedsAction = computed<boolean>(
    () =>
        (isConfirmed.value && props.payoutMissing) ||
        (!isConfirmed.value && props.applicationPhase !== 'pending'),
);

/**
 * Полный aria-label для SR. Видимая надпись всегда «Наставник» — статус и
 * нюанс фазы озвучиваем здесь.
 */
const mentorAriaLabel = computed<string>(() => {
    if (isConfirmed.value) {
        return props.payoutMissing
            ? 'Статус: наставник, карта для выплат не привязана — нажмите, чтобы привязать'
            : 'Статус: наставник, профиль подтверждён';
    }
    switch (props.applicationPhase) {
        case 'draft':
            return 'Статус: наставник, заявка не завершена';
        case 'pending':
            return 'Статус: наставник, заявка на подтверждении';
        case 'changes_requested':
            return 'Статус: наставник, требуются правки заявки';
        case 'rejected':
            return 'Статус: наставник, заявка отклонена';
        case 'revoked':
            return 'Статус: наставник, доступ отозван администратором';
        default:
            return 'Статус: наставник (не подтверждён)';
    }
});

// ── Тело tooltip ──────────────────────────────────────────────────────

const tooltipMessage = computed<string>(() => {
    if (isConfirmed.value) {
        return props.payoutMissing
            ? 'Карта для выплат не привязана — ваши платные услуги скрыты от учеников. Нажмите, чтобы привязать карту.'
            : 'Профиль наставника подтверждён.';
    }
    switch (props.applicationPhase) {
        case 'draft':
            return 'Вы не закончили оформление заявки наставника. Продолжите, чтобы отправить её на модерацию.';
        case 'pending':
            return 'Вы пока не являетесь полноценным наставником. Запись на ваши сессии откроется после того, как заявку подтвердят.';
        case 'changes_requested':
            return 'Модератор попросил правки. Откройте заявку и дополните недостающие поля.';
        case 'rejected':
            return props.rejectionReason
                ? `Заявка отклонена: ${props.rejectionReason}`
                : 'Заявка отклонена. Поправьте профиль и подайте её заново.';
        // Копия зеркалит платформенное уведомление mentor_app_revoked
        // («Доступ наставника приостановлен…»).
        case 'revoked':
            return 'Доступ наставника приостановлен. Причина и комментарий — в личном кабинете. Свяжитесь с поддержкой, если считаете это ошибкой.';
        default:
            return 'Вы пока не являетесь полноценным наставником.';
    }
});

// ── Взаимодействие ────────────────────────────────────────────────────

function onChipClick(): void {
    // Подтверждённый наставник без привязанной карты — приоритетнее всего,
    // отдаём отдельным каналом, чтобы контейнер навигировал в настройки
    // приёма денег без расширения Phase-енума.
    if (isConfirmed.value && props.payoutMissing) {
        // Мобильный (compact): на touch нет hover, поэтому первый тап
        // РАСКРЫВАЕТ tooltip («платные услуги никто не видит — привяжите
        // карту»), а уже повторный — ведёт в настройки выплат. На десктопе
        // tooltip доступен по hover, поэтому клик сразу навигирует (как было).
        if (props.compact && !tooltipOpen.value) {
            tooltipOpen.value = true;
            return;
        }
        tooltipOpen.value = false;
        emit('click-payout-attach');
        return;
    }
    // «На проверке» — действий нет, показываем модалку статуса прямо на месте
    // (работает в шапке/дашборде/меню без правок потребителей). Остальные
    // фазы по-прежнему отдаём наружу — потребитель навигирует (draft/rejected/
    // changes_requested → /become-mentor; approved → кабинет).
    if (props.applicationPhase === 'pending') {
        statusModalOpen.value = true;
        return;
    }
    emit('click-mentor-chip', props.applicationPhase);
}

/**
 * Vuetify v-tooltip закрывается по Escape, когда activator — focused
 * <button>. Дополнительно focus() на чип как защита, если Esc нажат пока
 * фокус был внутри тела tooltip.
 */
function onChipKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
        tooltipOpen.value = false;
        mentorChipRef.value?.focus();
    }
}
</script>

<style scoped>
.role-chips {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    /* Hit-area для WCAG 2.5.5 в мобильном drawer'е — визуально чип компактный,
       но обёртка несёт 32px высоты. */
    min-height: 32px;
}

.role-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.02em;
    white-space: nowrap;
    line-height: 1.2;
    border: 1px solid transparent;
    background: transparent;
    color: #525a7d;
    font-family: inherit;
}

button.role-chip {
    cursor: pointer;
    min-height: 32px;
}

/* Суффикс-статус: чуть легче основной надписи, отделён точкой в шаблоне. */
.role-chip__label {
    font-weight: 700;
}
.role-chip__suffix {
    font-weight: 600;
    opacity: 0.85;
}
/* Action-needed (заявка не подана / нужны правки / отклонена) — янтарный
   акцент, «требуется ваше действие». Перебивает серый цвет чипа. */
.role-chip__suffix--action {
    color: #c2740a;
    opacity: 1;
}
/* Подтверждённый наставник + payoutMissing — янтарный текст #c2740a нечитаем
   на фиолетовом #7B65F7. Берём светло-янтарный со слабым тёмным «карманом»,
   чтобы выделение оставалось заметным, но в рамках бренда. */
.role-chip--confirmed .role-chip__suffix--action {
    color: #ffd466;
    background: rgba(0, 0, 0, 0.18);
    padding: 2px 8px;
    border-radius: 999px;
    /* Перебиваем 0.85, который шаблон ставит для нейтральных суффиксов. */
    opacity: 1;
}

button.role-chip:focus-visible {
    outline: 2px solid #7b65f7;
    outline-offset: 2px;
}

/* «Ученик» — нейтрально-синий активный статус. */
.role-chip--student.role-chip--active {
    background: rgba(59, 130, 246, 0.12);
    color: #1d4ed8;
    border-color: transparent;
}

/* «Наставник» (не подтверждён) — серый. */
.role-chip--unconfirmed {
    background: rgba(102, 112, 133, 0.1);
    color: #525a7d;
    border-color: rgba(102, 112, 133, 0.28);
}

/* «Наставник» (подтверждён) — фирменный фиолетовый, заливка. */
.role-chip--confirmed {
    background: #7b65f7;
    color: #ffffff;
    border-color: transparent;
}

/* P3 (report4-14-07-26): компактный (мобильный) чип подтверждённого наставника
   без привязанной карты — тёплый ОРАНЖЕВЫЙ (перебивает фиолетовый confirmed).
   Заказчик: так «нужно действие» заметнее, чем фиолетовым чипом. Сплошная
   заливка + белый текст ⇒ читаемый контраст (~5:1) в обеих темах; цвет отличен
   и от фиолетового confirmed, и от красного error. Только compact; двойной
   класс (.role-chip--confirmed.role-chip--payout-alert) перебивает базу. */
.role-chip--confirmed.role-chip--payout-alert {
    background: #c2410c;
    border-color: #9a3412;
    color: #ffffff;
}

/* Pulse-dot для «не подтверждён, на модерации» — «дышащий» индикатор. */
.role-chip__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
    flex-shrink: 0;
}
.role-chip__dot--pulse {
    animation: role-chip-pulse 1.6s ease-in-out infinite;
}
/* P3: МИГАЮЩАЯ точка на ОРАНЖЕВОМ чипе — белая (контраст ~5:1 на #c2410c),
   с лёгким тёмным кольцом для глубины. Мигание привлекает внимание к «нужно
   подключить карту»; на десктопе точки нет — там текстовый суффикс. */
.role-chip__dot--payout {
    background: #ffffff;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.15);
    animation: role-chip-blink 1.4s ease-in-out infinite;
}
@keyframes role-chip-pulse {
    0%,
    100% {
        opacity: 1;
        transform: scale(1);
    }
    50% {
        opacity: 0.4;
        transform: scale(0.85);
    }
}
/* P3: плавное мигание payout-точки (opacity), цикл 1.4s — не агрессивно. */
@keyframes role-chip-blink {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0.25;
    }
}
/* prefers-reduced-motion: и «дышащий» pulse, и мигающая payout-точка
   становятся статичными — доступность (WCAG 2.3.3). */
@media (prefers-reduced-motion: reduce) {
    .role-chip__dot--pulse,
    .role-chip__dot--payout {
        animation: none;
    }
}

/* Тело tooltip */
.role-chip-tooltip {
    max-width: 280px;
    padding: 4px 8px;
    line-height: 1.4;
}
.role-chip-tooltip__text {
    margin: 0;
    font-size: 13px;
    color: inherit;
}
</style>
